import {t, Trans} from '@lingui/macro'

import {ListItem, tokens, Inline, ListIcon, Stack, List, Text} from '@pleo-io/telescope'

import {PlanTypeName} from '~/api/types/billing'
import type {PricingPlan} from '~/entitlement/use-pricing-plan'
import type {Step} from '~/feature-ui/guides/guide'
import type {ConditionedPlans} from '~/feature-ui/plan-pill/use-conditioned-plans'
import type Country from '~/locale/country'

import {
    CheckCircleIcon,
    LinedThroughText,
    EmptyCircleIcon,
    Illustration,
} from './how-trial-works.styles'
import FreeTrialIllustration from './images/free-trial.svg'
import MagicianIllustration from './images/magician.png'
import {Perks} from './perks'

interface HowTrialWorksStep extends Step {
    name: string
}
export const getSteps = ({
    pickedPlan,
    trialLength,
    country,
    isFdd,
    conditionedPlans,
}: {
    pickedPlan: PricingPlan
    trialLength: number
    country: Country
    isFdd: boolean
    conditionedPlans: ConditionedPlans
}): HowTrialWorksStep[] => {
    const essentialPlanName = PlanTypeName[conditionedPlans.essential.type]
    return [
        {
            key: 'step1',
            name: t`Free trial`,
            title:
                pickedPlan === 'pro'
                    ? t`Try Pleo ${essentialPlanName} for free`
                    : t`Try the best of Pleo for free`,
            sections: [
                {
                    key: 'step1-section1',
                    content: <Perks trialLength={trialLength} country={country} />,
                },
                {
                    key: 'step1-section2',
                    content: (
                        <Text as="p">
                            <Trans>
                                Easier spend management. Better expense visibility. More time saved
                                for you and your whole team.
                            </Trans>
                        </Text>
                    ),
                },
            ],
            illustration: (
                <Illustration>
                    <img src={FreeTrialIllustration} aria-hidden alt="" />
                </Illustration>
            ),
            illustrationBgColor: tokens.pink300,
        },
        {
            key: 'step2',
            name: t`Getting started`,
            title: t`How your trial works`,
            sections: [
                {
                    key: 'step2-section1',
                    content: (
                        <List>
                            <ListItem>
                                <Inline space={8} alignItems="center">
                                    <ListIcon as={CheckCircleIcon} />
                                    <Stack>
                                        <LinedThroughText weight="bold">
                                            <Trans>Sign up to Pleo</Trans>
                                        </LinedThroughText>
                                        <Text>
                                            <Trans>Great job, you're in.</Trans>
                                        </Text>
                                    </Stack>
                                </Inline>
                            </ListItem>
                            {!isFdd && (
                                <ListItem>
                                    <Inline space={8} alignItems="center">
                                        <EmptyCircleIcon />
                                        <Stack>
                                            <Text weight="bold">
                                                <Trans>Activate your account</Trans>
                                            </Text>
                                            <Text>
                                                <Trans>By verifying a few company details.</Trans>
                                            </Text>
                                        </Stack>
                                    </Inline>
                                </ListItem>
                            )}
                            <ListItem>
                                <Inline space={8} alignItems="center">
                                    <EmptyCircleIcon />
                                    <Stack>
                                        <Text weight="bold">
                                            <Trans>Start your {trialLength}-day free trial</Trans>
                                        </Text>
                                        <Text>
                                            <Trans>By adding money to your Pleo Wallet.</Trans>
                                        </Text>
                                    </Stack>
                                </Inline>
                            </ListItem>
                            <ListItem>
                                <Inline space={8} alignItems="center">
                                    <EmptyCircleIcon />
                                    <Stack>
                                        <Text weight="bold">
                                            {pickedPlan === 'pro'
                                                ? t`Your ${essentialPlanName} Subscription starts`
                                                : t`Day ${trialLength}: Choose your plan`}
                                        </Text>
                                        <Text>
                                            {pickedPlan === 'pro'
                                                ? t`After your trial ends, you'll remain on a paid ${essentialPlanName} subscription.`
                                                : t`Decide between free or paid plans after the trial`}
                                        </Text>
                                    </Stack>
                                </Inline>
                            </ListItem>
                        </List>
                    ),
                },
                {
                    key: 'step2-section2',
                    content: (
                        <Text as="p">
                            {pickedPlan === 'pro' ? (
                                t`You can cancel your trial anytime.`
                            ) : (
                                <Trans>
                                    <Text as="span" weight="bold">
                                        Cancel anytime.
                                    </Text>{' '}
                                    We'll remind you 5 days before your trial ends.
                                </Trans>
                            )}
                        </Text>
                    ),
                },
            ],
            illustration: (
                <Illustration>
                    <img src={MagicianIllustration} alt="" aria-hidden width="300" height="300" />
                </Illustration>
            ),
            illustrationBgColor: tokens.shade200,
        },
    ]
}
